import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { FileText } from 'lucide-react';
import { listVorgaenge, refreshCustomerBafa, type Vorgang } from '../../lib/vorgangApi';
import { VorgangAccordion } from './VorgangAccordion';

export function VorgaengeTab({ customerId, customerEmail }: { customerId: number; customerEmail?: string | null }) {
  const [vorgaenge, setVorgaenge] = useState<Vorgang[]>([]);
  const navigate = useNavigate();
  // Per geteiltem Deep-Link (?vorgang=…) → diesen Vorgang automatisch aufklappen.
  const [searchParams] = useSearchParams();
  const targetVorgangId = Number(searchParams.get('vorgang')) || null;
  // Immer nur EIN Vorgang offen — sonst stapeln sich mehrere Dokumentwände untereinander.
  const [offenerVorgang, setOffenerVorgang] = useState<number | null>(targetVorgangId);

  const load = () => { listVorgaenge(customerId).then(setVorgaenge).catch(() => setVorgaenge([])); };
  useEffect(() => {
    load();
    refreshCustomerBafa(customerId).then(() => load()).catch(() => {});
    /* eslint-disable-next-line */
  }, [customerId]);

  // Sortierung: nach der "#N" im Berichtstitel — #1 unten, aufsteigend nach oben.
  // Vorgänge ohne #N (z.B. noch ohne Bericht) wandern nach oben; bei Gleichstand
  // die neueren oben. Tiebreaker VOR createdAt: die BAFA-Vorgangsnummer selbst, denn
  // die ist die verlässliche Chronologie (kleinere Nummer = früher gestellt/eingegangen,
  // siehe memory bafa-vorgangsnummer-reihenfolge) — createdAt ist nur der Zeitpunkt, zu
  // dem WIR den Datensatz angelegt/zuletzt angefasst haben, was bei nachträglich per
  // Mail nummerierten Anträgen (findOrCreateVorgang-Promotion) von der echten BAFA-
  // Reihenfolge abweichen kann (Vorgang 105/106 FG Maschinenbau, 14.10.2026: 106 trug die
  // kleinere Nummer 1316746, wurde aber erst später in unserer DB aktualisiert und
  // erschien dadurch fälschlich über 105).
  const numOf = (v: Vorgang): number | null => {
    const m = /#\s*(\d+)/.exec(v.reports?.[0]?.berichtstitel || '');
    return m ? parseInt(m[1], 10) : null;
  };
  const vnOf = (v: Vorgang): number | null =>
    v.vorgangsnummer && /^\d+$/.test(v.vorgangsnummer) ? parseInt(v.vorgangsnummer, 10) : null;
  const sorted = [...vorgaenge].sort((a, b) => {
    const na = numOf(a), nb = numOf(b);
    if (na != null && nb != null) return nb - na;                 // höchste #N oben, #1 unten
    if (na != null) return 1;                                     // ohne #N nach oben
    if (nb != null) return -1;
    const vna = vnOf(a), vnb = vnOf(b);
    if (vna != null && vnb != null && vna !== vnb) return vnb - vna; // größere Nummer oben, kleinere unten
    return (b.createdAt || '').localeCompare(a.createdAt || '');  // sonst: neueste oben
  });

  return (
    <div>
      <div className="flex items-center justify-between gap-2 mb-4">
        <h4 className="text-sm font-bold text-slate-900 dark:text-white">Vorgänge <span className="font-normal text-slate-400">· {vorgaenge.length}</span></h4>
        <button onClick={() => navigate(`/editor?customerId=${customerId}`)}
          className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-md bg-blue-600 hover:bg-blue-700 text-white shrink-0">
          <FileText className="w-3.5 h-3.5" /> Neuer Bericht
        </button>
      </div>
      {vorgaenge.length === 0 ? (
        <p className="text-sm text-slate-500 dark:text-slate-400 py-1">Noch keine Vorgänge.</p>
      ) : (
        <div className="space-y-3">
          {sorted.map((v) => (
            <VorgangAccordion
              key={v.id}
              vorgang={v}
              onChanged={load}
              customerEmail={customerEmail}
              open={offenerVorgang === v.id}
              onToggle={() => setOffenerVorgang(id => (id === v.id ? null : v.id))}
              scrollBeimOeffnen={v.id === targetVorgangId}
            />
          ))}
        </div>
      )}
    </div>
  );
}
